import { useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { movies } from "@/mocks/movies";
import MovieCard from "@/components/feature/MovieCard";
import Pagination from "@/components/feature/Pagination";
import Breadcrumb from "@/components/feature/Breadcrumb";
import FilterBar from "@/pages/category/components/FilterBar";
import { usePageMeta } from "@/hooks/usePageMeta";

const PAGE_SIZE = 12;

const SORTS = [
  { key: "latest", label: "最新更新" },
  { key: "hot", label: "热度最高" },
  { key: "rating", label: "评分最高" },
];

export default function Category() {
  const { type } = useParams();
  const mediaType = type === "tv" ? "tv" : "movie";
  const typeLabel = mediaType === "movie" ? "电影" : "电视剧";

  const [searchParams, setSearchParams] = useSearchParams();
  const sort = searchParams.get("sort") || "latest";

  const [region, setRegion] = useState(searchParams.get("region") || "全部");
  const [year, setYear] = useState(searchParams.get("year") || "全部");
  const [genre, setGenre] = useState(searchParams.get("genre") || "全部");
  const [page, setPage] = useState(1);
  const [filtersOpen, setFiltersOpen] = useState(false);

  usePageMeta(
    `${typeLabel} - 后入影视网`,
    `后入影视网${typeLabel}频道，汇集最新${typeLabel}资源，支持按地区、年份、题材筛选与热度、评分排序，带你快速找到想看的${typeLabel}作品。`,
    `${typeLabel},最新${typeLabel},${typeLabel}大全,在线观看,后入影视`
  );

  const base = useMemo(
    () => movies.filter((item) => item.type === mediaType),
    [mediaType]
  );

  const regions = useMemo(
    () => ["全部", ...Array.from(new Set(base.map((item) => item.region)))],
    [base]
  );
  const years = useMemo(
    () => [
      "全部",
      ...Array.from(new Set(base.map((item) => String(item.year)))).sort(
        (a, b) => Number(b) - Number(a)
      ),
    ],
    [base]
  );
  const genres = useMemo(
    () => ["全部", ...Array.from(new Set(base.flatMap((item) => item.genres)))],
    [base]
  );

  const filtered = useMemo(() => {
    const list = base.filter(
      (item) =>
        (region === "全部" || item.region === region) &&
        (year === "全部" || String(item.year) === year) &&
        (genre === "全部" || item.genres.includes(genre))
    );
    const sorted = [...list];
    if (sort === "hot") sorted.sort((a, b) => b.hot - a.hot);
    else if (sort === "rating") sorted.sort((a, b) => b.rating - a.rating);
    else
      sorted.sort(
        (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
      );
    return sorted;
  }, [base, region, year, genre, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = filtered.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  const handleSelect = (
    key: "region" | "year" | "genre" | "sort",
    value: string
  ) => {
    setPage(1);
    if (key === "region") setRegion(value);
    else if (key === "year") setYear(value);
    else if (key === "genre") setGenre(value);
    else setSearchParams(value === "latest" ? {} : { sort: value });
  };

  const handleReset = () => {
    setRegion("全部");
    setYear("全部");
    setGenre("全部");
    setPage(1);
    setSearchParams({});
  };

  const intro =
    mediaType === "movie"
      ? "院线佳作、口碑佳片一网打尽，按地区、年份与题材快速筛选你想看的电影。"
      : "热播剧集、经典长剧持续更新，多维筛选助你轻松追剧不迷路。";

  return (
    <div className="pb-16">
      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full px-4 py-8 md:px-6 md:py-10">
          <Breadcrumb
            items={[{ label: "首页", to: "/" }, { label: typeLabel }]}
          />
          <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            {typeLabel}
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-secondary-600">{intro}</p>
        </div>
      </div>

      <div className="mx-auto w-full px-4 md:px-6">
        <div className="mt-6 flex items-center justify-between lg:hidden">
          <span className="text-sm text-secondary-600">
            共 {filtered.length} 部作品
          </span>
          <button
            type="button"
            onClick={() => setFiltersOpen((prev) => !prev)}
            className="flex items-center gap-1.5 rounded-md border border-background-300 px-3 py-2 text-sm text-foreground-700"
          >
            <i className="ri-filter-3-line"></i>
            筛选
            <i
              className={`${filtersOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}`}
            ></i>
          </button>
        </div>

        <FilterBar
          className={`mt-4 lg:mt-6 ${filtersOpen ? "block" : "hidden lg:block"}`}
          regions={regions}
          years={years}
          genres={genres}
          region={region}
          year={year}
          genre={genre}
          sort={sort}
          sorts={SORTS}
          onSelect={handleSelect}
          onReset={handleReset}
        />

        <div className="mt-6 hidden items-center justify-between lg:flex">
          <p className="text-sm text-secondary-600">
            筛选结果共 <span className="font-semibold text-accent-600">{filtered.length}</span> 部作品
          </p>
        </div>

        {pageItems.length > 0 ? (
          <>
            <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-6">
              {pageItems.map((item) => (
                <MovieCard key={item.id} item={item} />
              ))}
            </div>
            <Pagination
              page={currentPage}
              totalPages={totalPages}
              onChange={(next) => {
                setPage(next);
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
            />
          </>
        ) : (
          <div className="mt-10 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 py-16 text-center">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-secondary-400">
              <i className="ri-inbox-line text-3xl"></i>
            </span>
            <p className="mt-4 font-heading text-lg font-bold text-foreground-900">
              暂无相关资源
            </p>
            <p className="mt-2 text-sm text-secondary-500">
              换个筛选条件试试，或重置筛选查看全部内容
            </p>
            <button
              type="button"
              onClick={handleReset}
              className="mt-5 flex items-center gap-1.5 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-refresh-line"></i>
              重置筛选
            </button>
          </div>
        )}
      </div>
    </div>
  );
}